import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Dropdown, Menu, Tag, notification } from 'antd';
import { connect, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl, FormattedDate } from 'react-intl';

import { addProjectUser } from '../../../../actions/projectUserActions';
import {
  getProjectById,
  inviteToWorkspace,
  toggleInviteToProjectModal,
  approveAccessRequest,
  denyAccessRequest,
} from '../../../../actions';
import UserAvatar from '../../atoms/ticket-drawer/UserAvatar';
import WorkspaceMemberRow from '../../atoms/invite-modal/WorkspaceMemberRow';

const MODAL_STYLES = {
  content: { padding: 0, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.12)', boxShadow: '0 16px 48px rgba(99,102,241,0.14)' },
  mask: { backdropFilter: 'blur(2px)', background: 'rgba(15,15,25,0.35)' },
  header: { padding: '16px 20px 0', background: '#fff' },
  body: { padding: '12px 20px' },
  footer: { padding: '0 20px 16px', borderTop: '1px solid rgba(99,102,241,0.08)', paddingTop: 12 },
};

const roles = ['admin', 'member', 'guest'];

const InviteToProjectModal = ({
  isModalOpen,
  allUsers,
  currentWorkspace,
  project,
  selectedProject,
  workspaceRole,
  user
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const inputRef = useRef(null);

  const [search, setSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [role, setRole] = useState('member');
  const [nonMemberEmail, setNonMemberEmail] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [requests, setRequests] = useState(project?.accessRequests);
  const [focusedMember, setFocusedMember] = useState(-1);
  const [focusedTag, setFocusedTag] = useState(-1);

  const { 1: urlProjectId } = window.location.pathname.split('/');
  const workspaceId = currentWorkspace?._id;
  const projectId = selectedProject || urlProjectId;

  useEffect(() => {
    if (isModalOpen && projectId) {
      dispatch(getProjectById(projectId));
      setRequests(project?.accessRequests);
    }
  }, [isModalOpen, projectId]);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isModalOpen]);

  const handleProjectInvite = () => {
    const users = selectedMembers?.map((user) => {
      return {
        userId: user?.value || user?._id,
        email: user.email,
        role: role,
      }
    });

    if (users?.length) {
      dispatch(addProjectUser(
        { invitees: users, projectId: projectId, workspaceId: workspaceId },
        () => {
          getProjectById(projectId);
          setSelectedMembers([]);
        }
      ));
    }
  }

  const removeRequest = (request) => {
    const x = requests.filter(re => re?._id != request?._id);
    setRequests([...x]);
  }

  const approveMember = (request) => {
    dispatch(approveAccessRequest(request));
    removeRequest(request)
  }

  const denyMember = (request) => {
    dispatch(denyAccessRequest(request));
    removeRequest(request)
  }

  const handleInviteToWorkspace = () => {
    if (!nonMemberEmail) return;
    dispatch(inviteToWorkspace({
      workspaceId: workspaceId || '',
      invitees: [nonMemberEmail],
    }, () => {
      setNonMemberEmail(null);
    }));
  };

  const members = useMemo(() => {
    return allUsers?.map((participant) => ({
      value: participant._id,
      fullName: participant.fullName,
      email: participant.email,
      avi: participant.avi,
      role: 'member',
    }));
  }, [allUsers, project?.participants]);

  const filteredMembers = useMemo(() => {
    if (!search) return [];
    return members?.filter(
      (member) =>
        (member?.fullName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.email?.toLowerCase()?.includes(search?.toLowerCase())) &&
        !selectedMembers.some((u) => u?.email === member?.email)
    );
  }, [search, members, selectedMembers]);

  const addMember = (member) => {
    if (selectedMembers.some(m => m?.email === member?.email)) return;
    setSelectedMembers([...selectedMembers, { ...member, role }]);
    setSearch('');
    setNonMemberEmail(null);
  };

  const removeMember = (email) => {
    setSelectedMembers(selectedMembers.filter((u) => u.email !== email));
  };

  const closeModal = () => {
    dispatch(toggleInviteToProjectModal(false));
    setSearch('');
    setSelectedMembers([]);
    setRole('member');
    setNonMemberEmail(null);
  };

  const handleSearchEnter = () => {
    const trimmed = search.trim();
    const found = members.find((u) => u.email.toLowerCase() === trimmed.toLowerCase());
    if (found) {
      addMember(found);
    } else {
      setNonMemberEmail(trimmed);
      setSearch('');
    }
  };

  const ids = project?.participants?.map(m => m?._id);
  const me = project?.participants?.find(participant => participant?._id == user?._id)

  useEffect(() => {
    setFocusedMember(0);
  }, [filteredMembers]);

  useEffect(() => {
    const el = document.getElementById(`member-item-${focusedMember}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedMember]);
  const handleKeyDown = (e) => {
    const inputIsEmpty = search.trim() === '';
    const caretPosition = inputRef.current?.selectionStart;

    if (e.key === 'ArrowLeft') {
      if (caretPosition === 0) {
        e.preventDefault();
        if (focusedTag === -1 && selectedMembers.length > 0) {
          setFocusedTag(selectedMembers.length - 1);
        } else if (focusedTag > 0) {
          setFocusedTag((prev) => prev - 1);
        }
      }
    } else if (e.key === 'ArrowRight') {
      if (focusedTag >= 0) {
        e.preventDefault();
        if (focusedTag < selectedMembers.length - 1) {
          setFocusedTag((prev) => prev + 1);
        } else {
          setFocusedTag(-1);
          inputRef.current?.focus();
        }
      }
    }

    if (e.key === 'Backspace') {
      if (focusedTag >= 0 && selectedMembers[focusedTag]) {
        e.preventDefault();
        removeMember(selectedMembers[focusedTag].email);
        setFocusedTag((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (inputIsEmpty && selectedMembers.length > 0 && caretPosition === 0) {
        e.preventDefault();
        removeMember(selectedMembers[selectedMembers.length - 1].email);
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedMember((prev) =>
        prev < filteredMembers.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedMember((prev) =>
        prev > 0 ? prev - 1 : filteredMembers.length - 1
      );
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedMember >= 0 && filteredMembers[focusedMember]) {
        const selected = filteredMembers[focusedMember];
        if (!ids?.includes(selected?.value)) {
          addMember(selected);
          setFocusedMember(-1);
        }
      } else {
        handleSearchEnter();
      }
    }
  };

  const myRole = me?.role;
  const isAdmin = myRole == 'admin' || myRole == 'owner';
  const isGuest = myRole == 'guest';

  return (
    <Modal
      title={`${isGuest ? "" : intl.formatMessage({ id: 'SHARE', defaultMessage: 'Share' })} ${project?.title ?? 'project'}`}
      open={isModalOpen}
      onCancel={closeModal}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
      styles={MODAL_STYLES}
      className='custom-modal'
      footer={
        <div className="flex justify-end mb-2">
          <button
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/${project._id}/board`);
              notification.success({
                message: intl.formatMessage({ id: 'PROJECT_LINK_COPIED', defaultMessage: 'Project link copied to clipboard' }),
                placement: 'bottomRight',
              });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 14px',
              borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              background: 'rgba(99,102,241,0.07)', border: '1.5px solid rgba(99,102,241,0.18)',
              color: 'rgba(99,102,241,0.8)', transition: 'all 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.07)'}
          >
            <img src="/images/icons/copy.svg" alt="" style={{ width: 13, height: 13, filter: 'brightness(0) saturate(100%) invert(35%) sepia(70%) saturate(600%) hue-rotate(220deg) brightness(95%)' }} />
            <FormattedMessage id="COPY_PROJECT_LINK" defaultMessage="Copy Project Link" />
          </button>
        </div>}
    >
      {!isGuest && <div onKeyDown={handleKeyDown} tabIndex={0} className="flex pt-4 relative z-0 gap-1">
        <div className="flex items-center flex-row py-1 w-full px-2 rounded-lg" style={{ border: isFocused ? '1.5px solid rgba(99,102,241,0.5)' : '1.5px solid rgba(99,102,241,0.18)', boxShadow: isFocused ? '0 0 0 3px rgba(99,102,241,0.08)' : 'none', background: isFocused ? '#fff' : 'rgba(99,102,241,0.02)', transition: 'all 0.15s' }}>
          <div className="flex  items-center gap-1 w-[78%] flex-wrap">
            {selectedMembers.map((member, index) => (
              <Tag
                key={member?.email}
                closable
                onClose={() => removeMember(member?.email)}
                className={`flex items-center mr-1 gap-1 ${index === focusedTag ? "border border-indigo-400 bg-indigo-50" : ""
                  }`}
              >
                <UserAvatar
                  alt="User"
                  classNames="w-4 h-4 rounded-full object-cover mr-1"
                  fullName={member?.fullName}
                  userId={member?.value}
                  src={member?.avi}
                  styles={{ objectFit: 'cover' }}
                />
                {member?.fullName || member?.email}
              </Tag>
            ))}
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setFocusedTag(-1);
              }}
              placeholder={
                selectedMembers.length === 0
                  ? intl.formatMessage({
                    id: 'SEARCH_USERS_OR_EMAIL',
                    defaultMessage: 'Search users or enter email',
                  })
                  : ""
              }
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="min-w-14 max-w-full flex-1 border-none outline-none focus:outline-none bg-transparent placeholder-gray-400"
            />
          </div>
          <div className="w-[20%] flex justify-end">
            <Dropdown
              overlay={
                <Menu onClick={({ key }) => {
                  const updatedMembers = selectedMembers?.map(m => ({
                    ...m,
                    role: key
                  }));
                  setSelectedMembers(updatedMembers);
                  setRole(key);
                }}>
                  {roles.map((r) => (
                    <Menu.Item className="!text-xs" key={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </Menu.Item>
                  ))}
                </Menu>
              }
              trigger={["click"]}
              className="text-xs"
              disabled={!isAdmin}
            >
              <div className="flex flex-row items-center text-xs cursor-pointer py-1 gap-1 px-2 rounded" style={{ transition: 'background 0.1s' }} onMouseEnter={e => e.currentTarget.style.background='rgba(99,102,241,0.07)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <p>{role?.charAt(0)?.toUpperCase() + role?.slice(1)}</p>
                {isAdmin && <img
                  src="/images/icons/black-arrow-down.svg"
                  alt="icon"
                  className="mt-[2px] w-2 h-2"
                />}
              </div>
            </Dropdown>
          </div>
        </div>
        <button
          onClick={handleProjectInvite}
          disabled={selectedMembers.length === 0}
          style={{
            height: 36, padding: '0 14px', borderRadius: 9, border: 'none', marginLeft: 8,
            fontSize: 13, fontWeight: 600, color: '#fff', cursor: selectedMembers.length === 0 ? 'not-allowed' : 'pointer',
            background: selectedMembers.length === 0 ? 'rgba(99,102,241,0.35)' : '#4f46e5',
            boxShadow: selectedMembers.length > 0 ? '0 2px 8px rgba(79,70,229,0.28)' : 'none',
            transition: 'all 0.15s', flexShrink: 0,
          }}
        >
          <FormattedMessage id="ADD" defaultMessage="Invite" />
        </button>
      </div>}
      <div className="relative w-full">
        {search && filteredMembers.length > 0 && (
          <div className="absolute left-0 top-full mt-1 z-10 bg-white overflow-y-auto" style={{ border: '1px solid rgba(99,102,241,0.15)', borderRadius: 10, boxShadow: '0 8px 24px rgba(99,102,241,0.12)', maxHeight: 200, width: '85%' }}>
            {filteredMembers.map((member, index) => {
              const isFocused = index === focusedMember;
              return (
                <div
                  id={`member-item-${index}`}
                  key={member?.email}
                  onClick={() => {
                    if (!ids?.includes(member?.value)) {
                      addMember(member);
                    }
                  }}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2`}
                  style={{ borderBottom: '1px solid rgba(99,102,241,0.06)', background: isFocused ? 'rgba(99,102,241,0.07)' : '#fff', transition: 'background 0.1s' }}
                  onMouseEnter={e => { if (!isFocused) e.currentTarget.style.background = 'rgba(99,102,241,0.04)'; }}
                  onMouseLeave={e => { if (!isFocused) e.currentTarget.style.background = '#fff'; }}>
                  <UserAvatar
                    alt="User"
                    classNames="w-7 h-7 rounded-full object-cover mr-1"
                    fullName={member?.fullName}
                    userId={member?.value}
                    src={member?.avi}
                    styles={{ objectFit: 'cover' }}
                  />
                  {ids?.includes(member?.value) ?
                    <div className='flex align-center'>
                      <p>{member?.fullName}</p>
                      <div className='ml-3 px-2 py-0.5 rounded text-xs text-white' style={{ background: '#4f46e5' }}>Project member</div>
                    </div> :
                    <div>
                      <h4>{member?.fullName}</h4>
                      <p className='font-12 font-light'>{member?.email}</p>
                    </div>
                  }
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* {nonMemberEmail && (
        <div className="p-2 mt-2  flex justify-between items-center  bg-red-50">
          <p className=' text-red-500'>
            <FormattedMessage
              id="USER_NOT_IN_WORKSPACE"
              defaultMessage={`User ${nonMemberEmail || 'user'} is not in this workspace.`}
              values={{
                email: nonMemberEmail || intl.formatMessage({ id: 'USER', defaultMessage: 'user' }),
              }}
            />
          </p>
          <button
            onClick={handleInviteToWorkspace}
            className="bg-blue-500 hover:bg-blue-400 text-white rounded-md cursor-pointer px-3 py-1 items-center gap-1"
          >
            <FormattedMessage id="INVITE_TO_WORKSPACE" defaultMessage="Invite to workspace" />
          </button>
        </div>
      )} */}
      {requests?.map((request, i) => {
        return (
          <div key={request?._id}>
            {i == 0 && (
              <p className="font-semibold ml-2 text-lg py-2 mt-2">
                <FormattedMessage id="PROJECT_REQUESTS" defaultMessage="Requests" />
              </p>
            )}
            <div className='flex align-center justify-between p-2'>
              <div className='flex align-center'>
                <UserAvatar
                  alt="User"
                  classNames="rounded-full w-8 h-8 mr-3"
                  fullName={request?.userId?.fullName}
                  userId={request?.userId?._id}
                  src={request?.userId?.avi}
                  styles={{ objectFit: 'cover' }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: '600'
                    }}>
                    {request?.userId?.fullName || ''}
                  </div>
                  <div style={{ fontSize: 10 }}><FormattedMessage id="REQUESTED_DATE" defaultMessage="Requested Date" />: <FormattedDate value={request?.createdAt} /></div>
                </div>
              </div>
              <div className='flex align-center'>
                <div className='bg-[#0473cc] size-6 mr-1 rounded-full flex justify-center items-center cursor-pointer' onClick={() => approveMember(request)}>
                  <img src='/images/icons/thick-white.svg' className='size-3' />
                </div>
                <div className='bg-red-500 size-6 rounded-full flex justify-center items-center cursor-pointer' onClick={() => denyMember(request)}>
                  <img src='/images/icons/close.svg' className='size-2'
                    style={{ filter: 'invert(1) brightness(2)' }} />
                </div>
              </div>
            </div>
          </div>
        )
      })}
      <p className="font-semibold ml-2 text-lg py-2 mt-2">
        <span className='mr-1'>
          {project?.participants?.length}
        </span>
        <FormattedMessage id="MEMBERS" defaultMessage="Members" />
      </p>
      <div className='max-h-[40vh] overflow-y-auto'>
        <WorkspaceMemberRow
          key={user?._id}
          member={me}
          isProject={true}
          projectId={project?._id}
          isMember={!isAdmin}
        />
        {project?.participants?.map((member) => {

          if (member?._id == user?._id) {
            return;
          }

          return (
            <WorkspaceMemberRow
              key={member._id}
              member={member}
              isProject={true}
              projectId={project?._id}
              isMember={!isAdmin}
            />
          )
        })}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  allUsers: state?.userReducer?.users,
  currentWorkspace: state?.workspaceReducer?.currentWorkspace,
  project: state.projectReducer.getProjects,
  user: state.userReducer.user,
  workspaceRole: state.userReducer.role
});

export default connect(mapStateToProps)(InviteToProjectModal);
